import zipObject from 'lodash/zipObject';

import { isLeafNode } from './query';

/**
 * Prefix a fragment's leaves with a parent path.
 *
 * @param  {string} path Parent path to which to bind the fragment
 * @param  {object} fragment View fragment to be bound with the parent path
 * @return {object} Fragment bound to the parent path
 *
 * @example
 * bind('todosById[0..20]', todoView.todo)
 *
 * @example
 * bind('todosById[0..20]', {
 *   'name': 'name',
 *   'isCompleted': { $get: 'isCompleted' },
 *   'setIsCompleted': { $set: 'isCompleted' },
 *   'reorder': { $call: 'reorder' },
 * })
 */
function bind(path, fragment) {
  const prefixedFragment = getPrefixedFragment(path, fragment);
  return isRangePath(path) ? { '*': prefixedFragment } : prefixedFragment;
}

function isRangePath(path) {
  return path.endsWith(']');
}

function getPrefixedFragment(prefix, fragment) {
  if (isLeafNode(fragment)) { return getPrefixedNode(prefix, fragment); }
  const fragmentKeys = Object.keys(fragment);
  return zipObject(
    fragmentKeys.map((key) => key),
    fragmentKeys.map((key) => getPrefixedFragment(prefix, fragment[key]))
  );
}

function getPrefixedNode(prefix, value) {
  if (typeof value === 'string') {
    return getPrefixedPath(prefix, value);
  } else if (value && ('$get' in value)) {
    return { ...value, $get: getPrefixedPath(prefix, value.$get) };
  } else if (value && ('$set' in value)) {
    return { ...value, $set: getPrefixedPath(prefix, value.$set) };
  } else if (value && ('$call' in value)) {
    return { ...value, $call: getPrefixedPath(prefix, value.$call) };
  }
  /* istanbul ignore next */ return value;
}

function getPrefixedPath(prefix, path) {
  if (path.startsWith('[')) {
    return `${prefix}${path}`;
  }
  return `${prefix}.${path}`;
}

export default bind;
